// "Prices" card of the aircraft form: currency, hourly rates, contingency, minimums, peak uplift multipliers.
import { Button, Card, Col, DatePicker, Divider, Form, Row, Select, Space } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { AVIATION_CURRENCIES } from '../../api/types';
// SHAR-917 - numeric fields that refuse letters and symbols as they are typed.
import NumberInput from '../../components/NumberInput';
import { updateUplift, type Uplift } from './aircraftFormRows';
// Admin regression 6 Oct — the API's ceilings (MONEY_MAX; sector time 99 h; multiplier 10).
import { MONEY_MAX } from '../../data/limits';

export function PricesCard({
  uplifts,
  setUplifts,
}: {
  uplifts: Uplift[];
  setUplifts: React.Dispatch<React.SetStateAction<Uplift[]>>;
}) {
  return (
    <Card title="Prices" style={{ marginBottom: 16 }}>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            name="currencyCode"
            label="Currency"
          >
            <Select
              showSearch
              placeholder="Type to search…"
              optionFilterProp="label"
              options={AVIATION_CURRENCIES.map((c) => ({
                value: c.code,
                label: `${c.code} — ${c.name}`,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="occupiedHourlyRate"
            label="Occupied hourly rate"
          >
            <NumberInput
              min={0}
              precision={2}
              style={{ width: '100%' }}
              max={MONEY_MAX}
              placeholder="e.g. 4600"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="ferryHourlyRate" label="Ferry hourly rate">
            <NumberInput
              min={0}
              precision={2}
              style={{ width: '100%' }}
              max={MONEY_MAX}
              placeholder="e.g. 2600"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="contingencyTimeMin"
            label="Contingency time (minutes)"
          >
            <NumberInput min={0} style={{ width: '100%' }} placeholder="e.g. 15" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="minSectorTimeHours"
            label="Minimum sector time (hours)"
          >
            <NumberInput
              min={0}
              precision={2}
              style={{ width: '100%' }}
              max={99}
              placeholder="e.g. 1.0"
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="minTripRevenue"
            label="Minimum trip revenue"
          >
            <NumberInput
              min={0}
              precision={2}
              style={{ width: '100%' }}
              max={MONEY_MAX}
              placeholder="e.g. 6500"
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider>Peak uplift multipliers</Divider>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {uplifts.map((u, i) => (
          <Row gutter={8} align="middle" key={i}>
            <Col xs={24} md={7}>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="From"
                value={u.fromDate}
                onChange={(v) => updateUplift(setUplifts, i, { fromDate: v })}
              />
            </Col>
            <Col xs={24} md={7}>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="To"
                value={u.toDate}
                onChange={(v) => updateUplift(setUplifts, i, { toDate: v })}
              />
            </Col>
            <Col xs={24} md={6}>
              <NumberInput
                style={{ width: '100%' }}
                min={0}
                max={10}
                step={0.05}
                precision={3}
                placeholder="Multiplier (e.g. 1.25)"
                value={u.multiplier ?? undefined}
                onChange={(v) =>
                  updateUplift(setUplifts, i, { multiplier: v == null ? null : Number(v) })
                }
              />
            </Col>
            <Col xs={24} md={2}>
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={() => setUplifts((rs) => rs.filter((_, j) => j !== i))}
              />
            </Col>
          </Row>
        ))}
        <Button
          icon={<PlusOutlined />}
          onClick={() =>
            setUplifts((rs) => [...rs, { fromDate: null, toDate: null, multiplier: null }])
          }
        >
          Add peak period
        </Button>
      </Space>
    </Card>
  );
}
